import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';
import { ShimmerPanel } from '@/components/ui/Shimmer';

const getCorrelationColor = (corr) => {
  if (corr > 0.3) return '#059669';
  if (corr < -0.3) return '#dc2626';
  return '#6b7280';
};

const getChangeColor = (change) => {
  if (change > 0) return '#059669';
  if (change < 0) return '#dc2626';
  return '#6b7280';
};

const getTrendIcon = (marketTrend) => {
  if (marketTrend === 'bullish') return '📈';
  if (marketTrend === 'bearish') return '📉';
  return '📊';
};

export const StockMarketPanel = ({ moodData, stockData, loaded = false }) => {
  const [displayData, setDisplayData] = useState(null);
  const hasLoadedOnce = useRef(false);

  // Update display data only when we have new valid data
  useEffect(() => {
    if (stockData?.indexes?.length) {
      setDisplayData(stockData);
      hasLoadedOnce.current = true;
    }
  }, [stockData]);

  const indexes = displayData?.indexes || [];
  const marketTrend = displayData?.market_trend || 'unknown';
  const marketSentiment = displayData?.analysis?.market_sentiment || 'neutral';
  const dataSource = displayData?.data_source || 'Loading...';

  // Show shimmer only while the very first data response is still pending.
  if (!indexes.length) {
    if (!hasLoadedOnce.current && !loaded) {
      return <ShimmerPanel type="stocks" />;
    }
    return (
      <div className="stock-market-panel" data-testid="stock-market-panel">
        <div className="stock-header">
          <TrendingUp size={14} />
          <span>Mood vs Markets</span>
        </div>
        <div className="empty-state">Loading market data...</div>
      </div>
    );
  }

  return (
    <div className="stock-market-panel" data-testid="stock-market-panel">
      <div className="stock-header">
        <TrendingUp size={14} />
        <span>Mood vs Markets</span>
        <span className="stock-trend">{getTrendIcon(marketTrend)} {marketTrend}</span>
      </div>
      <div className="stock-indexes">
        {indexes.map((index, idx) => (
          <motion.div 
            key={index.symbol}
            className="stock-index-row"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
          >
            <div className="stock-name">
              <span className="stock-symbol">{index.symbol}</span>
              <span className="stock-fullname">{index.name}</span>
            </div>
            <div className="stock-values">
              <span className="stock-change" style={{ color: getChangeColor(index.change) }}>
                {index.change > 0 ? '+' : ''}{(index.change || 0).toFixed(2)}%
              </span>
              <span className="stock-correlation" style={{ color: getCorrelationColor(index.correlation) }}>
                r={(index.correlation || 0).toFixed(2)}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="stock-legend">
        <span>{marketSentiment === 'risk-off' ? '⚠️ Risk-Off' : marketSentiment === 'risk-on' ? '✅ Risk-On' : '➖ Neutral'}</span>
        <span className="stock-source">{dataSource}</span>
      </div>
    </div>
  );
};
